import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import * as XLSX from 'xlsx';
import LoadingProgressBar from '../Loading-ProgressBar';
import ErrorMsgPopup from '../modal-popup/errormsgpopup';
import transformColumnsForTanStack from '../extendedcommondatagrid/transformColumnsForTanStack';
// ---------------------------------------------------------------------------------------------------------

ExportToExcelButton.propTypes = {
    rowData: PropTypes.array,
    columnData: PropTypes.array,
};
// ---------------------------------------------------------------------------------------------------------

export default function ExportToExcelButton({ rowData, columnData }) {

    const [isExporting, setIsExporting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [errorPopup, setErrorPopup] = useState(false);

    const handelErrorPopup = () => {
        setErrorMsg('');
        setErrorPopup(false);
    };

    // Detect if incoming columns are from MUI DataGrid
    const isMuiGridColumns = useMemo(
        () => columnData?.some(col => col.field && !col.accessorKey),
        [columnData]
    );

    const buildCellParamsForExport = (value, row) => ({
        value,
        row,
        getValue: () => value
    });

    const formatVariantMap = useMemo(() =>
        Object.fromEntries(
            columnData.map(col => [col.accessorKey, col.meta?.formatVariant])
        ), [columnData]);

    const formatDateValue = (value, accessorKey) => {
        if (!value) return "";

        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;

        const pad = (n) => String(n).padStart(2, "0");
        const day = pad(d.getDate());
        const month = d.toLocaleString("en-US", { month: "short" });
        const year = d.getFullYear();
        const hh = pad(d.getHours());
        const mm = pad(d.getMinutes());
        const ss = pad(d.getSeconds());

        // ---- MUI grid formatting ----
        if (isMuiGridColumns) {
            const column = columnData.find(
                col => col.field === accessorKey || col.accessorKey === accessorKey
            );

            const headerText = `${column?.headerName || ""} ${column?.field || ""}`;

            if (/month/i.test(headerText)) {
                return `${month}-${year}`;
            }

            const hasTimeInValue = typeof value === "string" && /\d{1,2}:\d{2}(:\d{2})?/.test(value);
            const hasNonZeroTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
            const hasTime = hasTimeInValue && hasNonZeroTime;

            return hasTime ? `${day}-${month}-${year} ${hh}:${mm}:${ss}` : `${day}-${month}-${year}`;
        }

        // ---- TanStack formatting ----
        const formatVariant = formatVariantMap[accessorKey] || "date";

        switch (formatVariant) {
            case "datetime":
                return `${day}-${month}-${year} ${hh}:${mm}:${ss}`;

            case "monthdate":
                return `${month}-${year}`;

            case "date":
            default:
                return `${day}-${month}-${year}`;
        }

    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Convert MUI columns ONLY when required
            // const finalColumns = !isMuiGridColumns(columnData) ? transformColumnsForTanStack(columnData, rowData) : columnData;

            const filteredColumns = columnData.filter(col => {
                const key = col.accessorKey ?? col.field;
                return (
                    key &&
                    !["actions", "__check__", "id"].includes(key) &&
                    (col.showInGrid !== false || col.exportable)
                );
            });

            const sanitizedColumns = filteredColumns.map((col) => {
                const headerText = col.header?.trim() || col.headerName?.trim() || "";

                return {
                    ...col,
                    header: headerText.replace(/\n/g, " "),
                    accessorKey: col.accessorKey || col.field,
                };
            });

            const exportHeaders = sanitizedColumns.map((col) => col.header || col.accessorKey);
            const exportFields = sanitizedColumns.map((col) => col?.accessorKey || col?.field);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([]);
            XLSX.utils.sheet_add_aoa(worksheet, [exportHeaders], { origin: "A1" });
            const currentRow = 1;

            const keywordsRegex = /value|amount|price|stdnr|rate|actualnr|totaldiff|items|quantity|qty|stock|purchase|return|adjustment|inword|outword|jnjsale|interdealersale|unitgts|subtotal|inward|outward|conversionFactor|territorySeqNo/i;
            const excludeKeyword = "Date";

            const reorderedData = rowData?.map((item) =>
                // const reorderedItem = [];

                exportFields.map((key) => {
                    const col = sanitizedColumns.find((column) => column.accessorKey === key);
                    if (!col) return "";

                    const value = item[key];
                    const rendererType = col.meta?.rendererType;

                    try {
                        const headerNameMatches = col?.header && keywordsRegex.test(col.header) && col.header !== excludeKeyword;
                        const fieldMatches = col?.accessorKey && keywordsRegex.test(col.accessorKey) && col.accessorKey !== excludeKeyword;
                        const isDateColumn = /(date|month)/i.test(col.header || "") || /(date|month)/i.test(col.accessorKey || "");

                        // Handle renderCell for TanStack columns
                        if (rendererType === "renderCell" && typeof col.cell === "function") {
                            const renderedValue = col.cell(buildCellParamsForExport(value, item));

                            let extractedValue = value;

                            if (React.isValidElement(renderedValue)) {
                                if (typeof renderedValue.props?.children === "string") {
                                    extractedValue = renderedValue.props.children;
                                }
                            }
                            else if (renderedValue !== undefined && renderedValue !== null) {
                                extractedValue = renderedValue;
                            }

                            return isDateColumn
                                ? formatDateValue(extractedValue, col.accessorKey)
                                : extractedValue;
                        }
                        // Handle valueFormatter for TanStack columns
                        if (rendererType === "valueFormatter" && typeof col.cell === "function") {
                            if ((headerNameMatches || fieldMatches) && !isDateColumn || col?.accessorKey === "distributorMargin") {
                                return value;
                            }
                            if (isDateColumn) {
                                return formatDateValue(value, col.accessorKey);
                            }

                            return col.cell({ value, row: item });
                        }
                        // Handle renderCell for MUI columns
                        if (col.renderCell && typeof col.renderCell === "function") {
                            const renderedValue = col.renderCell({ value, row: item });
                            if (React.isValidElement(renderedValue)) {
                                return typeof renderedValue.props?.children === "string"
                                    ? renderedValue.props.children
                                    : value;
                            }
                            return renderedValue ?? value;
                        }
                        // Handle valueFormatter for MUI columns
                        if (col.valueFormatter && typeof col.valueFormatter === "function") {
                            if ((headerNameMatches || fieldMatches) && !isDateColumn) {
                                return value;
                            }
                            if (isDateColumn) {
                                return formatDateValue(value, col.accessorKey);
                            }
                            return col.valueFormatter({ value, row: item });
                        }

                        return value ?? "";
                    }
                    catch (err) {
                        return "";
                    }
                })
            );

            XLSX.utils.sheet_add_aoa(worksheet, reorderedData, { origin: { r: currentRow, c: 0 } });

            // Apply number formatting
            if (worksheet["!ref"]) {
                const range = XLSX.utils.decode_range(worksheet["!ref"]);

                for (let c = 0; c < exportFields.length; c += 1) {
                    const col = sanitizedColumns[c];
                    for (let r = 1; r <= range.e.r; r += 1) {
                        const cellAddress = XLSX.utils.encode_cell({ r, c });
                        const cell = worksheet[cellAddress];

                        const headerNameMatches = col?.header && keywordsRegex.test(col.header) && col.header !== excludeKeyword;
                        const fieldMatches = col?.accessorKey && keywordsRegex.test(col.accessorKey) && col.accessorKey !== excludeKeyword;
                        const isDateColumn = /(date|month)/i.test(col.header || "") || /(date|month)/i.test(col.accessorKey || "");

                        if ((headerNameMatches || fieldMatches) && !isDateColumn) {
                            const numericValue = parseFloat(String(cell?.v).replace(/,/g, ""));
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = ["conversionFactor", "territorySeqNo"].includes(col.accessorKey)
                                    ? "0"
                                    : "#,##0.00";
                            }
                        }

                        if (col?.accessorKey === "dos") {
                            cell.t = "n";
                            cell.z = "#,##0";
                        }

                        if (col?.accessorKey === "distributorMargin") {
                            const numericValue = parseFloat(String(cell.v).replace("%", "")) / 100;
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = "0.00%";
                            }
                        }

                        if (col?.accessorKey === "distributorCode") {
                            const numericValue = parseFloat(cell.v);
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = "0";
                            }
                        }
                    }
                }
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            const pageTitle = document.title;
            const formattedDate = new Date().toISOString().slice(0, 10);
            const fileName = `${pageTitle}_${formattedDate}.xlsx`;

            XLSX.writeFile(workbook, fileName);
        } catch (error) {
            console.error("Error exporting data to Excel:", error);
            setErrorMsg("Error exporting data to Excel. Please try again.");
            setErrorPopup(true);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Button className='buttonStyle' variant='contained' size="small"
                sx={{ color: '#fff !important' }} onClick={exportToExcel}>
                Export
            </Button>

            {isExporting && <LoadingProgressBar />}

            {errorPopup && <ErrorMsgPopup confirm={handelErrorPopup} msg={errorMsg} header="Error" />}
        </>
    );
}